import {
  formatDateTime,
  nextMeetingInputSchema,
  nextMonthStart,
  type Meeting,
  type NextMeetingInput,
} from '@fluxus/shared';
import { useId, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { localParts, startsAtFrom } from '@/features/meetings/time';
import { zodFieldErrors, type FieldErrors } from '@/lib/forms';

/**
 * Encerrar a reunião (T-122b; DEC-051): a ata é gerada e a próxima reunião, agendada — sempre na análise crítica
 * (com data e título sugeridos, ajustáveis por quem pode agendar), a pedido nas outras. `next` undefined deixa o
 * servidor sugerir; null não agenda.
 */
export function CloseMeetingDialog({
  meeting,
  open,
  canSchedule,
  busy,
  serverErrors,
  onConfirm,
  onCancel,
}: {
  meeting: Meeting;
  open: boolean;
  /** "Criar" em Reuniões: pode informar a próxima. */
  canSchedule: boolean;
  busy: boolean;
  /** Erros do servidor nos campos da próxima (next.title, next.startsAt). */
  serverErrors: FieldErrors;
  onConfirm: (next: NextMeetingInput | null | undefined) => void;
  onCancel: () => void;
}) {
  const ids = { title: useId(), day: useId(), time: useId() };
  const suggested = localParts(nextMonthStart(meeting.startsAt));
  const analysis = meeting.type === 'CRITICAL_ANALYSIS';
  const [schedule, setSchedule] = useState(analysis);
  const [title, setTitle] = useState(meeting.title);
  const [day, setDay] = useState(suggested.day);
  const [time, setTime] = useState(suggested.time);
  const [errors, setErrors] = useState<FieldErrors>({});
  const shown = {
    title: errors.title ?? serverErrors['next.title'],
    day: errors.startsAt ?? serverErrors['next.startsAt'],
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSchedule) return onConfirm(analysis ? undefined : null);
    if (!schedule) return onConfirm(null);
    const next = { title, startsAt: day && time ? startsAtFrom(day, time) : '' };
    const parsed = nextMeetingInputSchema.safeParse(next);
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    onConfirm(parsed.data);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onCancel()}
      busy={busy}
      title="Encerrar a reunião"
      className="w-[min(32rem,calc(100vw-2rem))]"
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4 text-sm">
        <p>
          Ao encerrar, a ata é gerada com o que foi tratado e a reunião fica só para consulta.
          {analysis ? ' A próxima análise crítica é agendada com os mesmos participantes, nível e área.' : ''}
        </p>
        {!canSchedule ? (
          analysis ? (
            <p className="rounded-md bg-muted px-3 py-2">
              A próxima análise crítica será agendada para {formatDateTime(nextMonthStart(meeting.startsAt))},
              com o mesmo título.
            </p>
          ) : null
        ) : (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-2 font-semibold">Próxima reunião</legend>
            {analysis ? null : (
              <label className="inline-flex min-h-11 items-center gap-2">
                <input
                  type="checkbox"
                  className="size-4 accent-(--brand)"
                  checked={schedule}
                  onChange={(e) => setSchedule(e.target.checked)}
                />
                Agendar a próxima reunião
              </label>
            )}
            {schedule ? (
              <>
                <FormField id={ids.title} label="Título da próxima" error={shown.title}>
                  <Input
                    {...fieldA11y(ids.title, shown.title)}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </FormField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField id={ids.day} label="Data da próxima" error={shown.day}>
                    <Input
                      {...fieldA11y(ids.day, shown.day)}
                      type="date"
                      value={day}
                      onChange={(e) => setDay(e.target.value)}
                    />
                  </FormField>
                  <FormField id={ids.time} label="Hora da próxima">
                    <Input id={ids.time} type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                  </FormField>
                </div>
              </>
            ) : null}
          </fieldset>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy}>
            Encerrar e gerar a ata
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
